import { useEffect, useState } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PricingCard from '../PricingPage/PricingCard';
import { getPackages } from '../../services/package';
import { selectPlan, selectTrial } from '../../services/subscription';
import { getCookie, showError, removeCookie } from '../../utils';
import { getProfile } from '../../services/profile';
import { pageInfoAnalytics } from '../../config/mixpanelConfig';
import mixpanel from 'mixpanel-browser';
import store from '../../store';
import styles from './SimplePricing.module.css';

const SimplePricing = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const { profile } = useSelector((state) => {
    return state.profile;
  });
  const [isYearly, setIsYearly] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isFreeAccount = location?.state?.isFreeAccount === true;
  const fromDemo = new URLSearchParams(location.search).get('fromDemo') === 'true';
  const billingType = isYearly ? 'year' : 'month';

  const filteredPackages = isFreeAccount
    ? packages?.filter((item) => item?.name === 'Starter' && item?.type !== 'skypoint')
    : packages?.filter(({ type, name }) => type !== 'skypoint' && !name?.startsWith('Starter'));

  useEffect(() => {
    getAllPackages();
    pageInfoAnalytics('Simple Pricing Page Viewed', 'Simple Pricing Page');
  }, []);

  const getAllPackages = async () => {
    const data = await getPackages(profile?.user?.id);
    if (data) {
      setLoadingPackages(false);
      const sortedByCost = data?.packages?.sort((a, b) => Number(a.cost) - Number(b.cost));
      setPackages(sortedByCost);
    }
  };

  const onStart = async (id) => {
    if (profile?.business || fromDemo) {
      setIsLoading(true);
      const resp = await selectPlan({ id, billingType });
      if (resp) setIsLoading(false);
      if (resp?.data?.package) {
        navigate('/payInfo', {
          state: {
            stripePlan: resp?.data?.package?.stripeId,
            planId: resp?.data?.package?.id,
            plan: resp?.data?.package,
            isFreeAccount: isFreeAccount,
            subscriptionType: billingType,
            monthlyPlan: filteredPackages?.find(
              (item) => item?.name === resp?.data?.package?.name && item?.duration === 'month'
            ),
            yearlyPlan: filteredPackages?.find(
              (item) => item?.name === resp?.data?.package?.name && item?.duration === 'year'
            ),
            taxDetails: resp?.data?.taxDetails,
          },
        });
      }
    } else showError('Please create your business profile first.');
  };

  const onTrial = async (id) => {
    if (profile?.business || fromDemo) {
      setIsLoading(true);
      const resp = await selectTrial({ id });
      if (resp) {
        setIsLoading(true);
        let pr = await getProfile();
        if (pr) navigate('/home');
      }
    } else showError('Please create your business profile first.');
  };

  const onBack = () => {
    if (profile?.user) {
      navigate('/login');
    }
  };

  const handleLogout = () => {
    removeCookie('token');
    removeCookie('skypoint_token');
    localStorage.removeItem('selectedClient');
    localStorage.removeItem('isProviderView');
    mixpanel.track('Logout');
    mixpanel.reset();
    store.dispatch({ type: 'RESET_STORE' });
    navigate('/');
  };

  if (!isFreeAccount) {
    if (getCookie('token')) {
      if (profile?.user?.type !== 'staff')
        if (profile?.subscription) return <Navigate to="/home" />;
    }
  }

  const handleCardClick = (id, item) => {
    // if (Number(item?.cost) === 0) {
    //   onTrial(id);
    // } else {
      onStart(id);
    // }
  };

  const mapPackageToPlan = (pkg) => {
    const cost = Number(pkg.cost);
    const isFree = cost === 0;
    const facilityStr = Number(pkg.facility) > 1 ? `Up to ${pkg.facility} facilities` : '1 Facility';
    const name = pkg.name?.replace(/ - Yearly$/, '');

    const features = [facilityStr];

    if (name === 'Starter') {
      features.push(`${pkg.staff} staff members`);
    } else {
      features.push(`${pkg.staff} staff members (Add. +$${pkg.additionalStaff || '10'})`);
    }

    if (name === 'Starter') {
      features.push(`${pkg.patient} Clients`);
    } else {
      features.push(`${pkg.patient} Clients (Add. +$${pkg.additionalClient || '15'})`);
    }

    features.push('Medication Management/eMAR');
    features.push('Care Plans');
    features.push('Client Intake');
    features.push('Custom Roles');
    features.push('Document Management');
    features.push('Staff Management');
    features.push('Task Management');
    features.push('Text to Speech');
    features.push(`${pkg.notes} Notes`);
    features.push(`${pkg.reports} Reports`);
    features.push('Clock In/Clock Out');
    features.push('Invoice');
    features.push('EVV (paid feature)');

    if (name === 'Starter' || name === 'Bronze') {
      features.push('SkAI (100 queries)');
    }
    if (name === 'Silver') {
      features.push('Billing');
      features.push('SkAI (300 queries)');
      features.push('Scheduler (coming soon)');
    }
    if (name === 'Gold') {
      features.push('Billing');
      features.push('SkAI (Unlimited queries)');
      features.push('Scheduler (coming soon)');
    }

    const showTrial = (!profile || !profile?.user?.isTrialUsed) && pkg.duration !== 'year';
    if (showTrial) {
      features.push('14 days free trial');
    }

    const buttonText = showTrial ? 'Try for free' : 'Get Started';
    const isPopular = name === 'Bronze';

    return {
      name,
      description: getDescription(name),
      monthly: {
        price: `$${cost}`,
        period: '/month',
      },
      yearly: {
        price: `$${cost}`,
        period: '/year',
      },
      features,
      buttonText,
      buttonVariant: isPopular ? 'yellow' : 'black',
      isPopular,
      _id: pkg.id,
      _raw: pkg,
    };
  };

  const getDescription = (name) => {
    const descriptions = {
      Starter: 'Entry level for small providers.',
      Bronze: 'Perfect for growing agencies.',
      Silver: 'For scaling teams with billing needs.',
      Gold: 'Comprehensive solution for large facilities.',
    };
    return descriptions[name] || '';
  };

  const displayPackages = filteredPackages
    ?.filter((item) => item?.duration === billingType)
    ?.map(mapPackageToPlan);

  return (
    <div className={styles.page}>
      {/* Top bar */}
      <div className={styles.topBar}>
        {!isFreeAccount && (
          <button type="button" className={styles.backButton} onClick={onBack}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
        )}
        <button type="button" className={styles.logoutButton} onClick={handleLogout}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Logout
        </button>
      </div>

      {/* Hero */}
      <div className={styles.hero}>
        <div className={styles.header}>
          <h1 className={styles.title}>Select a plan</h1>
          <p className={styles.subtitle}>Choose the package that suits your needs</p>
        </div>
        <div className={styles.toggle}>
          <div className={styles.toggleWrapper}>
            <div className={`${styles.toggleBg} ${isYearly ? styles.toggleBgRight : ''}`} />
            <button
              type="button"
              className={`${styles.toggleButton} ${!isYearly ? styles.toggleButtonActive : ''}`}
              onClick={() => setIsYearly(false)}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`${styles.toggleButton} ${isYearly ? styles.toggleButtonActive : ''}`}
              onClick={() => setIsYearly(true)}
            >
              Yearly <span className={styles.toggleDiscount}>(10% off)</span>
            </button>
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className={styles.cardsSection}>
        <div className={styles.container}>
          {loadingPackages ? (
            <div className={styles.loadingGrid}>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={styles.skeleton} />
              ))}
            </div>
          ) : (
            <div className={isFreeAccount ? styles.singleCardGrid : styles.grid}>
              {displayPackages?.map((plan) => (
                <PricingCard
                  key={plan.name}
                  plan={plan}
                  isYearly={isYearly}
                  onClick={() => handleCardClick(plan._id, plan._raw)}
                  disabled={isLoading}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimplePricing;
